import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { Box, Card, CardContent, Typography, Avatar } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import { mainPageRootStyle } from '../styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadGrowthByHandle } from './actions';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { predictFollowersCount } from '../utils/predictionUtils';
import { formatNumber } from '../utils/numberUtils';
import { DateRangeFilter } from '../components/DateRangeFilter';
import HandleSelector from '../components/HandleSelector';
import { filterDataByDateRange } from '../utils/dateRangeUtils';
import SkeletonChart from '../components/SkeletonChart';
import SkeletonMetricCards from '../components/SkeletonMetricCards';
import EmptyState from '../components/EmptyState';
import Breadcrumb from '../components/Breadcrumb';


function DashboardPage() {
    const dispatch = useDispatch();
    const handles = useSelector(state => state.handle.handles);
    const growth = useSelector(state => state.growth.growthByHandle);
    const loading = useSelector(state => state.growth.loading);
    const userId = useSelector(state => state.auth.user?.uid);
    const navigate = useNavigate();
    const [dateRange, setDateRange] = useState('3months');

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedHandle = queryParams.get('handle');

    useEffect(() => {
        document.title = 'Growth - Sky Toolbox';
    }, []);

    // Auto-select first handle if none is selected
    useEffect(() => {
        if (!selectedHandle && handles && handles.length > 0) {
            navigate(`/growth?handle=${handles[0].handle}`, { replace: true });
        }
    }, [selectedHandle, handles, navigate]);

    useEffect(() => {
        dispatch(loadGrowthByHandle(userId, selectedHandle));
    }, [dispatch, selectedHandle, userId]);

    const handleSelectedHandleChange = (event) => {
        navigate(`/growth?handle=${event.target.value}`);
    };

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
    };

    const growthWithDateObjects = useMemo(() => {
        return (growth ?? []).map(item => ({
            ...item,
            date: new Date(item.date),
        })).sort((a, b) => a.date - b.date);
    }, [growth]);

    const filteredGrowthData = useMemo(() => {
        return filterDataByDateRange(growthWithDateObjects, dateRange);
    }, [growthWithDateObjects, dateRange]);

    const [followersIn3Months, followersIn6Months, followersIn1Year] = predictFollowersCount(growthWithDateObjects);
    
    // Get current followers count from the most recent data point (not filtered)
    const currentFollowers = growthWithDateObjects.length > 0 
        ? growthWithDateObjects[growthWithDateObjects.length - 1]?.stat?.followersCount || 0
        : 0;

    const isLoading = loading || !selectedHandle;
    const hasData = filteredGrowthData && filteredGrowthData.length > 0;

    // Color palette for different metrics
    const chartColors = {
        'stat.followersCount': { stroke: '#0085ff', fill: 'url(#colorFollowers)' },
        'stat.followsCount': { stroke: '#7c3aed', fill: 'url(#colorFollowings)' },
        'stat.postsCount': { stroke: '#10b981', fill: 'url(#colorPosts)' },
        'stat.newFollowsCount': { stroke: '#f59e0b', fill: 'url(#colorNewFollows)' },
        'stat.newLikesCount': { stroke: '#ef4444', fill: 'url(#colorNewLikes)' },
        'stat.newMentionsCount': { stroke: '#8b5cf6', fill: 'url(#colorNewMentions)' },
        'stat.newQuotesCount': { stroke: '#06b6d4', fill: 'url(#colorNewQuotes)' },
        'stat.newReplysCount': { stroke: '#ec4899', fill: 'url(#colorNewReplys)' },
        'stat.newRepostsCount': { stroke: '#14b8a6', fill: 'url(#colorNewReposts)' },
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    }}
                >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {new Date(label).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                        })}
                    </Typography>
                    {payload.map((entry, index) => (
                        <Typography 
                            key={index} 
                            variant="body2" 
                            sx={{ color: entry.color }}
                        >
                            {entry.name}: <strong>{formatNumber(entry.value)}</strong>
                        </Typography>
                    ))}
                </Box>
            );
        }
        return null;
    };

    const renderChart = (dataKey, name) => {
        const colors = chartColors[dataKey] || { stroke: '#8884d8', fill: 'url(#colorDefault)' };
        const gradientId = colors.fill.match(/url\(#(.+)\)/)?.[1] || 'colorDefault';
        
        return (
            <Card elevation={2} sx={{ mb: 4, p: 2, width: '100%' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, ml: 2 }}>
                    {name}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                        data={filteredGrowthData}
                        margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                    >
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={colors.stroke} stopOpacity={0.3}/>
                                <stop offset="95%" stopColor={colors.stroke} stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid 
                            strokeDasharray="3 3" 
                            stroke="#e2e8f0"
                            strokeOpacity={0.5}
                        />
                        <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                            })}
                            stroke="#94a3b8"
                            style={{ fontSize: '0.875rem' }}
                        />
                        <YAxis 
                            stroke="#94a3b8"
                            style={{ fontSize: '0.875rem' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            wrapperStyle={{ 
                                paddingTop: '20px',
                                fontSize: '0.875rem',
                                fontWeight: 600
                            }}
                        />
                        <Line 
                            type="monotone" 
                            dataKey={dataKey} 
                            name={name} 
                            stroke={colors.stroke}
                            strokeWidth={3}
                            fill={colors.fill}
                            dot={{ r: 4, strokeWidth: 2, fill: '#ffffff' }}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                            animationDuration={1000}
                            animationEasing="ease-in-out"
                            connectNulls={true}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Card>
        );
    };

    return (
        <Paper style={mainPageRootStyle} sx={{ p: 3 }}>
            <Breadcrumb items={[
                { label: 'Growth Dashboard' }
            ]} />
            
            {/* Page Header */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    color: 'white',
                }}
            >
                <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                    📈 Growth Dashboard
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Track your follower growth and predict future trends
                </Typography>
            </Box>
            
            <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <HandleSelector
                    value={selectedHandle}
                    onChange={handleSelectedHandleChange}
                    handles={handles}
                    ariaLabel="Select handle to view growth metrics"
                />
                <DateRangeFilter 
                    value={dateRange} 
                    onChange={handleDateRangeChange}
                    sx={{ mt: 0, mb: 0 }}
                />
            </Box>

            {isLoading ? (
                <SkeletonMetricCards count={4} />
            ) : (
                <Box display="flex" justifyContent="center" flexWrap="wrap" gap={3} sx={{ mb: 4 }}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            width: '240px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                <PersonIcon fontSize="large" />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {formatNumber(currentFollowers)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Current Followers
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card 
                        elevation={0}
                        sx={{ 
                            width: '240px',
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(240, 147, 251, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                <TrendingUpIcon fontSize="large" />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {formatNumber(followersIn3Months)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                In 3 Months
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card 
                        elevation={0}
                        sx={{ 
                            width: '240px',
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(79, 172, 254, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                <TrendingUpIcon fontSize="large" />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {formatNumber(followersIn6Months)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                In 6 Months
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card 
                        elevation={0}
                        sx={{ 
                            width: '240px',
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(250, 112, 154, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 48, height: 48, mb: 2 }}>
                                <TrendingUpIcon fontSize="large" />
                            </Avatar>
                            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {formatNumber(followersIn1Year)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                In 1 Year
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {isLoading ? (
                <SkeletonChart count={3} />
            ) : !hasData ? (
                <EmptyState
                    title="No data available"
                    message="Select a handle and date range to view growth metrics"
                />
            ) : (
                <>
                    {renderChart('stat.followersCount', 'Followers')}
                    {renderChart('stat.followsCount', 'Followings')}
                    {renderChart('stat.postsCount', 'Posts')}
                    {renderChart('stat.newFollowsCount', 'New Follows')}
                    {renderChart('stat.newLikesCount', 'New Likes')}
                    {renderChart('stat.newMentionsCount', 'New Mentions')}
                    {renderChart('stat.newQuotesCount', 'New Quotes')}
                    {renderChart('stat.newReplysCount', 'New Replys')}
                    {renderChart('stat.newRepostsCount', 'New Reposts')}
                </>
            )}
        </Paper >
    );
}

export default DashboardPage;
